import { forwardRef, type ElementRef, type ReactNode } from 'react';
import {
  Switch as HUISwitch,
  Field,
  Label,
  Description,
  type SwitchProps as HUISwitchProps,
} from '@headlessui/react';
import { cn } from '../../../lib/cn.js';

export interface SwitchProps extends Omit<
  HUISwitchProps,
  'children' | 'as' | 'checked' | 'onChange'
> {
  checked: boolean;
  onChange: (value: boolean) => void;
  /** Optional visible label rendered to the left of the switch. */
  label?: ReactNode;
  /** Optional help text below the label. */
  description?: ReactNode;
  /** Accessible label when `label` is not rendered visibly. */
  'aria-label'?: string;
  className?: string | undefined;
  /** Class for the switch itself (root). Overrides sizing when needed. */
  switchClassName?: string;
}

/**
 * Design-token wrapper around Headless UI's Switch.
 * Checked: `bg-accent-cyan` — Unchecked: `bg-white/10`.
 *
 * When `label` / `description` are provided, the component is wrapped in a
 * Headless UI `Field` so `aria-labelledby` / `aria-describedby` are wired
 * automatically.
 */
export const Switch = forwardRef<ElementRef<typeof HUISwitch>, SwitchProps>(function Switch(
  { checked, onChange, label, description, className, switchClassName, disabled, ...rest },
  ref,
) {
  const control = (
    <HUISwitch
      ref={ref}
      checked={checked}
      onChange={onChange}
      {...(disabled !== undefined ? { disabled } : {})}
      className={cn(
        'group relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-white/10 transition-colors duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        checked ? 'bg-accent-cyan border-accent-cyan/40' : 'bg-white/10 hover:bg-white/15',
        disabled && 'cursor-not-allowed opacity-60',
        switchClassName,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-150',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </HUISwitch>
  );

  if (!label && !description) {
    return <div className={className}>{control}</div>;
  }

  return (
    <Field as="div" className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0 flex-1">
        {label ? (
          <Label className="block text-small font-medium text-primary">{label}</Label>
        ) : null}
        {description ? (
          <Description className="mt-0.5 block text-caption leading-relaxed text-secondary">
            {description}
          </Description>
        ) : null}
      </div>
      <div className="flex-shrink-0 pt-0.5">{control}</div>
    </Field>
  );
});
